import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  useToast,
} from "@asm/ui";
import { Download, Loader2, CheckCircle2, Upload, FileText, X } from "lucide-react";
import { adminApi, ReconImportRequest, ReconImportResponse } from "@/lib/api";

interface ReconImportProps {
  onClose: () => void;
  onComplete: () => void;
}

type SourceType = "tranco" | "majestic" | "umbrella" | "custom" | "csv_upload";

const SOURCES = [
  {
    id: "tranco" as const,
    name: "Tranco",
    description: "Research-focused top 1M list (aggregated from multiple sources)",
  },
  {
    id: "majestic" as const,
    name: "Majestic",
    description: "Majestic Million - domains by linking root domains",
  },
  {
    id: "umbrella" as const,
    name: "Cisco Umbrella",
    description: "Umbrella Popularity List - DNS query popularity",
  },
  {
    id: "custom" as const,
    name: "Custom URL",
    description: "Import from a custom CSV URL",
  },
  {
    id: "csv_upload" as const,
    name: "CSV Upload",
    description: "Upload a CSV file from your computer",
  },
];

export function ReconImport({ onClose, onComplete }: ReconImportProps) {
  const { toast } = useToast();
  const [source, setSource] = useState<SourceType>("tranco");
  const [customUrl, setCustomUrl] = useState("");
  const [limit, setLimit] = useState(10000);
  const [targetTlds, setTargetTlds] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ReconImportResponse | null>(null);

  // CSV upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string>("");
  const [csvPreviewLines, setCsvPreviewLines] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [".csv", ".txt", ".tsv"];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (!validTypes.includes(ext) && file.type !== "text/csv" && file.type !== "text/plain") {
      toast({
        title: "Invalid file",
        description: "Please select a CSV or text file (.csv, .txt, .tsv)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 50MB",
        variant: "destructive",
      });
      return;
    }

    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setCsvContent(text);

      // Show first 5 lines as preview
      const lines = text.split("\n").filter((l) => l.trim().length > 0);
      setCsvPreviewLines(lines.slice(0, 5));
    };
    reader.readAsText(file);
  };

  const clearFile = () => {
    setCsvFile(null);
    setCsvContent("");
    setCsvPreviewLines([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (source === "custom" && !customUrl) {
      toast({
        title: "Error",
        description: "Please enter a custom URL",
        variant: "destructive",
      });
      return;
    }

    if (source === "csv_upload" && !csvContent) {
      toast({
        title: "Error",
        description: "Please select a CSV file to upload",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    try {
      const request: ReconImportRequest = {
        source,
        limit: limit > 0 ? limit : undefined,
        url: source === "custom" ? customUrl : undefined,
        csv_content: source === "csv_upload" ? csvContent : undefined,
        target_tlds: targetTlds
          ? targetTlds.split(",").map((t) => t.trim()).filter(Boolean)
          : undefined,
      };

      const response = await adminApi.importReconDomains(request);
      setResult(response);

      toast({
        title: "Import Complete",
        description: response.message,
      });
    } catch (err) {
      toast({
        title: "Import Failed",
        description: err instanceof Error ? err.message : "Failed to import domains",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onComplete();
    } else {
      onClose();
    }
  };

  const csvLineCount = csvContent ? csvContent.split("\n").filter((l) => l.trim().length > 0).length : 0;

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Domains</DialogTitle>
          <DialogDescription>
            Import domains from public domain lists or upload a CSV file
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-6">
            <div className="flex flex-col items-center text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold">Import Complete</h3>
              <p className="text-muted-foreground mt-1">
                {result.message}
              </p>
              <div className="mt-4 p-4 rounded-lg bg-muted text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-muted-foreground">Source:</span>
                    <span className="ml-2 font-medium">{result.source}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Imported:</span>
                    <span className="ml-2 font-medium">{result.imported.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Source Selection */}
            <div className="space-y-2">
              <Label>Source</Label>
              <div className="grid grid-cols-2 gap-2">
                {SOURCES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setSource(s.id);
                      if (s.id !== "csv_upload") clearFile();
                    }}
                    className={`p-3 text-left rounded-lg border transition-colors ${
                      source === s.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/50"
                    } ${s.id === "csv_upload" ? "col-span-2" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      {s.id === "csv_upload" && <Upload className="h-4 w-4" />}
                      <div className="font-medium text-sm">{s.name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {s.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom URL */}
            {source === "custom" && (
              <div className="space-y-2">
                <Label htmlFor="customUrl">Custom URL</Label>
                <Input
                  id="customUrl"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://example.com/domains.csv"
                />
                <p className="text-xs text-muted-foreground">
                  CSV format: rank,domain or one domain per line
                </p>
              </div>
            )}

            {/* CSV Upload */}
            {source === "csv_upload" && (
              <div className="space-y-2">
                <Label>CSV File</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.tsv,text/csv,text/plain"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {csvFile ? (
                  <div className="space-y-3">
                    {/* Selected file info */}
                    <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{csvFile.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {(csvFile.size / 1024).toFixed(1)} KB &middot; {csvLineCount.toLocaleString()} lines
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={clearFile} className="flex-shrink-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Preview */}
                    {csvPreviewLines.length > 0 && (
                      <div className="rounded-md border bg-muted/30 p-2">
                        <div className="text-xs font-medium text-muted-foreground mb-1">Preview:</div>
                        <div className="font-mono text-xs space-y-0.5 max-h-24 overflow-y-auto">
                          {csvPreviewLines.map((line, i) => (
                            <div key={i} className="text-muted-foreground truncate">
                              {line}
                            </div>
                          ))}
                          {csvLineCount > 5 && (
                            <div className="text-muted-foreground/50">
                              ... and {(csvLineCount - 5).toLocaleString()} more lines
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-muted-foreground/50" />
                    <div className="text-sm text-muted-foreground">
                      Click to select a CSV file
                    </div>
                    <div className="text-xs text-muted-foreground/75">
                      Supports .csv, .txt, .tsv &middot; Max 50MB
                    </div>
                  </button>
                )}
                <p className="text-xs text-muted-foreground">
                  One domain per line, or CSV with rank,domain format. Lines starting with # are ignored.
                </p>
              </div>
            )}

            {/* Limit */}
            <div className="space-y-2">
              <Label htmlFor="limit">Domain Limit</Label>
              <Input
                id="limit"
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
                min={0}
                max={1000000}
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of domains to import. Set to 0 for no limit.
              </p>
            </div>

            {/* TLD Filter */}
            <div className="space-y-2">
              <Label htmlFor="targetTlds">Filter by TLD (optional)</Label>
              <Input
                id="targetTlds"
                value={targetTlds}
                onChange={(e) => setTargetTlds(e.target.value)}
                placeholder="com, io, net"
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of TLDs. Leave empty to import all.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={importing}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    {source === "csv_upload" ? (
                      <Upload className="mr-2 h-4 w-4" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Import
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
